import { useEffect, useRef, useState } from "react";
import { useLang } from "@/i18n";
import api, { errMsg } from "@/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Link2, Trash2, Download } from "lucide-react";

export default function MaterialsTab() {
  const { t } = useLang();
  const [materials, setMaterials] = useState([]);
  const [students, setStudents] = useState([]);
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("file");
  const [form, setForm] = useState({ title: "", title_hu: "", description: "", url: "", shared: true, student_ids: [] });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    api.get("/admin/materials").then((r) => setMaterials(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/admin/students").then((r) => setStudents(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const toggleStudent = (id) => {
    setForm((f) => ({ ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter((s) => s !== id) : [...f.student_ids, id] }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "file") {
        if (!file) { toast.error("Choose a file"); setBusy(false); return; }
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("title_hu", form.title_hu);
        fd.append("description", form.description);
        fd.append("shared", form.shared ? "true" : "false");
        fd.append("student_ids", form.student_ids.join(","));
        await api.post("/admin/materials/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        await api.post("/admin/materials/link", {
          title: form.title, title_hu: form.title_hu, description: form.description,
          url: form.url, shared: form.shared, student_ids: form.student_ids,
        });
      }
      toast.success(t("admin.materialAdded"));
      setOpen(false);
      setForm({ title: "", title_hu: "", description: "", url: "", shared: true, student_ids: [] });
      setFile(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/materials/${id}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="materials-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-serif font-semibold text-lg">{t("admin.materials")}</h2>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="add-material-btn"><Plus className="w-4 h-4 mr-1" /> {t("admin.newMaterial")}</Button>
      </div>

      <div className="mt-4 grid sm:grid-cols-2 gap-4">
        {materials.length === 0 && <p className="text-muted-foreground">{t("admin.noData")}</p>}
        {materials.map((m) => (
          <Card key={m.id} data-testid={`admin-material-${m.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2.5">
                  {m.type === "file" ? <FileText className="w-5 h-5 text-primary mt-0.5" /> : <Link2 className="w-5 h-5 text-teal-600 mt-0.5" />}
                  <div>
                    <p className="font-semibold text-sm">{m.title}{m.title_hu ? ` · ${m.title_hu}` : ""}</p>
                    {m.description && <p className="text-xs text-muted-foreground mt-0.5">{m.description}</p>}
                    {m.original_filename && <p className="text-xs text-muted-foreground">{m.original_filename}</p>}
                  </div>
                </div>
                <button onClick={() => remove(m.id)} className="text-muted-foreground hover:text-destructive" data-testid={`material-delete-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 flex items-center justify-between">
                <Badge variant={m.shared ? "secondary" : "outline"} className="text-xs">
                  {m.shared ? t("admin.sharedAll") : `${t("admin.assignedOnly")} (${m.student_ids.length})`}
                </Badge>
                {m.type === "file" && (
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={async () => {
                    try {
                      const res = await api.get(`/materials/${m.id}/download`, { responseType: "blob" });
                      const url = URL.createObjectURL(res.data);
                      const a = document.createElement("a");
                      a.href = url; a.download = m.original_filename || m.title; a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) { toast.error(errMsg(e)); }
                  }} data-testid={`material-admin-download-${m.id}`}>
                    <Download className="w-3.5 h-3.5 mr-1" /> {t("dash.download")}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="material-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl">{t("admin.newMaterial")}</DialogTitle></DialogHeader>
          <div className="flex gap-2">
            <Button size="sm" variant={mode === "file" ? "default" : "outline"} onClick={() => setMode("file")} data-testid="material-mode-file">{t("admin.fileUpload")}</Button>
            <Button size="sm" variant={mode === "link" ? "default" : "outline"} onClick={() => setMode("link")} data-testid="material-mode-link">{t("admin.linkAdd")}</Button>
          </div>
          <form onSubmit={submit} className="space-y-3 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("admin.titleLabel")}</Label>
                <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="material-title-input" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("admin.titleHuLabel")}</Label>
                <Input value={form.title_hu} onChange={(e) => setForm({ ...form, title_hu: e.target.value })} data-testid="material-title-hu-input" /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">{t("admin.descLabel")}</Label>
              <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="material-desc-input" /></div>
            {mode === "file" ? (
              <div className="space-y-1"><Label className="text-xs">File (PDF, DOC, MP3… max 25MB)</Label>
                <Input ref={fileRef} type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="material-file-input" /></div>
            ) : (
              <div className="space-y-1"><Label className="text-xs">{t("admin.urlLabel")}</Label>
                <Input required type="url" placeholder="https://..." value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="material-url-input" /></div>
            )}
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={form.shared} onCheckedChange={(v) => setForm({ ...form, shared: !!v })} data-testid="material-shared-checkbox" />
              {t("admin.sharedAll")}
            </label>
            {!form.shared && (
              <div className="max-h-36 overflow-y-auto border border-border rounded-lg p-2 space-y-1.5" data-testid="material-student-picker">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.student_ids.includes(s.id)} onCheckedChange={() => toggleStudent(s.id)} data-testid={`material-student-${s.id}`} />
                    {s.name} <span className="text-xs text-muted-foreground">{s.email}</span>
                  </label>
                ))}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={busy} data-testid="material-submit-btn">{busy ? t("common.loading") : t("admin.add")}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
